<script setup>
// =============================================================================
// FangateItemEditModal — full Vault-detail-style editor inside a side-panel modal
//
// Opened from the "Edit item" pencil button in FangateProductCard. Mirrors the
// Vault detail view's form: title, descriptions, price, folder, compliance
// flags, co-creator consent tagging. Same 4-endpoint save flow:
//   PATCH /products/{id}              metadata + flags (NOT in_collection)
//   PATCH /products/{id}/price        price only (when changed)
//   PATCH /products/{id}/folder       folder_id (when changed)
//   POST  /products/{id}/collection   toggle in/out (when changed)
//   POST  /consents/store-or-send     per new co-creator
//
// Backdrop is non-dismissive — user must hit the prominent "Close ×" button.
// Stays open after Save (refreshes initial snapshots) so the user can keep
// editing without re-opening.
// =============================================================================

import { ref, reactive, computed, watch, nextTick } from 'vue';
import FangateAPI from 'dashboard/api/fangate';

const props = defineProps({
  open:           { type: Boolean, default: false },
  // The product currently being edited — passed in from the parent (panel
  // resolves which variant is active). Shape: { id, title, price, link,
  // public_description, internal_notes, folder_id, folder, in_collection,
  // is_adult_content, is_verif_age, is_downloadable, is_should_consent }.
  product:        { type: Object,  default: null },
  // Currently-selected variant (when the product is part of a multi-link
  // cluster). Used so "Preview link" opens the URL of the variant the
  // user is actually editing, not the top-level product.link.
  // Shape: { id, title, price, url }
  activeLink:     { type: Object,  default: null },
  isConnected:    { type: Boolean, default: false },
  formatBalance:  { type: Function, default: n => String(n) },
  currencySymbol: { type: String,  default: '€' },
});

const emit = defineEmits([
  'update:open',
  // Payload: the merged product after save — parent updates its grid card.
  'saved',
  // Convenience: open the FangatePriceModal — parent already owns it
  // (so price change + new-link variants stay routed through one place).
  'request-edit-price',
]);

const FANGATE_MIN_PRICE = 5;
const FANGATE_MAX_PRICE = 224;

// ---- Form state -----------------------------------------------------------
const state = reactive({
  title:               '',
  publicDesc:          '',
  internalNotes:       '',
  price:               '',
  initialPrice:        0,
  folderId:            null,
  initialFolderId:     null,
  flags: {
    avs18:        false,
    agecheck:     false,
    download:     true,
    collection:   false,
    shouldConsent:false,
  },
  initialInCollection: false,
  saving:   false,
  error:    '',
  // Folder picker UI
  folderPickerOpen: false,
  // Sub-mode within the open picker — 'pick' shows the list, 'new' shows
  // an inline input to create a new folder via POST /content-folders.
  folderMode:       'pick',
  newFolderName:    '',
  newFolderSaving:  false,
});

// Folder list — loaded from /content-folders. Defensive: API may 403 if
// the token lacks the relevant ability; the picker then just shows the
// existing folder name without alternatives.
const apiFolders = ref([]);
const loadFolders = async () => {
  if (!props.isConnected) { apiFolders.value = []; return; }
  try {
    const list = await FangateAPI.listContentFolders();
    apiFolders.value = Array.isArray(list) ? list : [];
  } catch (_) {
    apiFolders.value = [];
  }
};
const folderNameOf = id => {
  if (id == null) return '';
  const f = apiFolders.value.find(x => Number(x.id) === Number(id));
  return f?.name || '';
};
const folderLabel = computed(() => folderNameOf(state.folderId) || 'No folder');

// ---- Consent tags + search ------------------------------------------------
const coTags = reactive({
  items: [],
  loading: false,
  error: '',
});
const coTagSearch = reactive({ query: '', results: [], loading: false, error: '' });
const taggingId = ref(null);
let coTagSearchTimer = null;

// Inline title editor in the modal header (mirrors Vault detail UX):
// click "Edit title" → bold title swaps to an input; Enter/Escape/blur commits.
const editingTitle = ref(false);
const titleInputRef = ref(null);
const startEditTitle = async () => {
  editingTitle.value = true;
  await nextTick();
  titleInputRef.value?.focus?.();
  titleInputRef.value?.select?.();
};
const commitEditTitle = () => { editingTitle.value = false; };

// Preview URL — should point at the CONTENT (where the file lives) rather
// than the checkout link the buyer would see. We fall through the most
// likely fields Fangate exposes:
//   1. media[].source      → direct file URL (S3 signed link, usually)
//   2. media[].preview_url → preview-image URL
//   3. preview / thumb     → product-level preview image
//   4. link                → checkout URL (last-resort fallback)
const previewUrl = computed(() => {
  const p = props.product;
  if (!p) return '';
  const media = Array.isArray(p.media) ? p.media : [];
  const first = media[0] || {};
  return first.source
      || first.preview_url
      || first.url
      || p.preview
      || p.thumb
      || p.link
      || '';
});
const openPreview = () => {
  const url = previewUrl.value;
  if (!url) return;
  window.open(url, '_blank', 'noopener,noreferrer');
};

// "Preview link" is the public checkout URL — what a buyer sees. Separate
// from "View content" (the actual content the creator uploaded). Prefers
// the parent-supplied activeLink so multi-variant products open the URL
// of the CURRENT variant rather than the top-level product.link.
const checkoutUrl = computed(() => {
  const variantUrl = props.activeLink?.url;
  if (variantUrl) return variantUrl;
  const p = props.product;
  if (!p) return '';
  return p.link || p.url || '';
});
const openCheckout = () => {
  const url = checkoutUrl.value;
  if (!url) return;
  window.open(url, '_blank', 'noopener,noreferrer');
};

const creatorLabelOf = c => {
  if (!c) return 'Unknown';
  if (c.display_name) return c.display_name;
  const full = [c.first_name, c.last_name].filter(Boolean).join(' ').trim();
  return full || c.email || `Creator #${c.id ?? '?'}`;
};
const getInitials = label => {
  const s = String(label || '').trim();
  if (!s) return '?';
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return s.slice(0, 2).toUpperCase();
};
const isEmailLike = s => typeof s === 'string' && /^\S+@\S+\.\S+$/.test(s.trim());
// Email-invite hidden (audit #5): Fangate accepts the consent POST but does NOT
// actually send the invite email — a silent no-op. Mirrors the Vault (#97).
// Tag-by-Fangate-creator still works. Re-enable once Fangate confirms delivery.
const showEmailInvite = computed(() => false);

const loadConsentTags = async () => {
  if (!props.product?.id || !props.isConnected) { coTags.items = []; return; }
  coTags.loading = true; coTags.error = '';
  try {
    const tags = await FangateAPI.listProductConsentTags(props.product.id);
    coTags.items = Array.isArray(tags) ? tags : [];
  } catch (e) {
    coTags.items = [];
    const msg = String(e?.message || '');
    if (!msg.includes('HTTP 404')) coTags.error = msg || 'Failed to load tags';
  } finally {
    coTags.loading = false;
  }
};
const onCoTagInput = () => {
  if (coTagSearchTimer) clearTimeout(coTagSearchTimer);
  coTagSearchTimer = setTimeout(runCoTagSearch, 220);
};
const runCoTagSearch = async () => {
  const q = String(coTagSearch.query || '').trim();
  if (q.length < 2) { coTagSearch.results = []; coTagSearch.error = ''; return; }
  coTagSearch.loading = true; coTagSearch.error = '';
  try {
    const r = await FangateAPI.searchCreators(q);
    coTagSearch.results = Array.isArray(r) ? r : [];
  } catch (e) {
    coTagSearch.results = [];
    coTagSearch.error = e?.message || 'Search failed';
  } finally {
    coTagSearch.loading = false;
  }
};
const tagCreatorFromResult = async creator => {
  if (!props.product?.id || !creator?.id) return;
  taggingId.value = creator.id;
  try {
    await FangateAPI.addProductConsentTag({
      productId:       props.product.id,
      taggedCreatorId: creator.id,
      date:            new Date().toISOString().slice(0, 10),
    });
    await loadConsentTags();
    coTagSearch.query = ''; coTagSearch.results = [];
  } catch (e) {
    coTagSearch.error = e?.message || 'Failed to tag co-creator';
  } finally {
    taggingId.value = null;
  }
};
const invitingEmail = ref(false);
const inviteByEmail = async () => {
  const email = String(coTagSearch.query || '').trim();
  if (!props.product?.id || !isEmailLike(email)) return;
  invitingEmail.value = true;
  try {
    await FangateAPI.addProductConsentTag({
      productId: props.product.id,
      email,
      date:      new Date().toISOString().slice(0, 10),
    });
    await loadConsentTags();
    coTagSearch.query = ''; coTagSearch.results = [];
  } catch (e) {
    coTagSearch.error = e?.message || 'Failed to send invite';
  } finally {
    invitingEmail.value = false;
  }
};

// ---- Open/Reset -----------------------------------------------------------
// Seed the form from the product when the modal opens. Capture
// `initial*` snapshots so the save flow knows what actually changed.
watch(() => props.open, isOpen => {
  if (!isOpen || !props.product) return;
  const p = props.product;
  state.title         = p.title || '';
  state.publicDesc    = p.public_description || p.description || '';
  state.internalNotes = p.internal_notes || '';
  state.price         = p.price != null ? String(p.price) : '';
  state.initialPrice  = Number(p.price) || 0;
  state.folderId        = p.folder_id ?? p.folder?.id ?? null;
  state.initialFolderId = state.folderId;
  state.flags.avs18         = !!(p.is_adult_content);
  state.flags.agecheck      = !!(p.is_verif_age);
  state.flags.download      = p.is_downloadable !== false;
  state.flags.collection    = !!(p.in_collection);
  state.initialInCollection = state.flags.collection;
  state.flags.shouldConsent = !!(p.is_should_consent);
  state.error  = '';
  state.saving = false;
  state.folderPickerOpen = false;
  state.folderMode       = 'pick';
  state.newFolderName    = '';
  state.newFolderSaving  = false;

  loadFolders();
  loadConsentTags();
});

const close = () => {
  emit('update:open', false);
  // Strip focus from any element that retained it so the trigger button
  // doesn't get a focus-ring on close.
  if (typeof document !== 'undefined') document.activeElement?.blur?.();
};

// AVS off forcibly clears agecheck (matches upload modal rule).
const toggleFlag = key => {
  state.flags[key] = !state.flags[key];
  if (key === 'avs18' && !state.flags.avs18) state.flags.agecheck = false;
};

// Folder picker
const folderOptions = computed(() =>
  [...(apiFolders.value || [])]
    .filter(f => f && f.name)
    .sort((a, b) => String(a.name).localeCompare(String(b.name)))
);
const pickFolder = f => {
  state.folderId = f ? Number(f.id) : null;
  state.folderPickerOpen = false;
  // After picking, drop out of "new folder" mode if it was open.
  state.folderMode  = 'pick';
  state.newFolderName = '';
};

// Inline "+ New folder" — same flow as Vault detail. Switching to 'new'
// mode swaps the picker's bottom row for a name input + Save button.
const startNewFolder = () => {
  state.folderMode    = 'new';
  state.newFolderName = '';
};
const cancelNewFolder = () => {
  state.folderMode    = 'pick';
  state.newFolderName = '';
};
const confirmNewFolder = async () => {
  const name = String(state.newFolderName || '').trim();
  if (!name) return;
  state.newFolderSaving = true;
  try {
    const created = await FangateAPI.createContentFolder(name);
    if (created?.id) {
      apiFolders.value = [...apiFolders.value, created];
      pickFolder(created);
    }
    state.folderMode    = 'pick';
    state.newFolderName = '';
  } catch (e) {
    state.error = e?.message || 'Failed to create folder.';
  } finally {
    state.newFolderSaving = false;
  }
};

// Save: 3 separate endpoints + consent tag handling.
const save = async () => {
  const p = props.product;
  if (!p) return;
  const rawPrice = String(state.price || '').trim();
  const priceEur = parseFloat(rawPrice);
  if (rawPrice === '' || isNaN(priceEur) ||
      priceEur < FANGATE_MIN_PRICE || priceEur > FANGATE_MAX_PRICE) {
    state.error = `Price must be between ${props.formatBalance(FANGATE_MIN_PRICE)} and ${props.formatBalance(FANGATE_MAX_PRICE)}.`;
    return;
  }
  state.saving = true;
  state.error  = '';

  const metaPayload = {
    title:               state.title || p.title,
    public_description:  state.publicDesc,
    internal_notes:      state.internalNotes,
    is_adult_content:    state.flags.avs18,
    is_verif_age:        state.flags.agecheck,
    is_downloadable:     state.flags.download,
    is_should_consent:   state.flags.shouldConsent,
  };
  const priceCents        = Math.round(priceEur * 100);
  const priceChanged      = Math.abs(priceEur - Number(state.initialPrice || 0)) > 0.001;
  const folderChanged     =
    Number(state.folderId ?? -1) !== Number(state.initialFolderId ?? -1);
  const collectionChanged = state.flags.collection !== state.initialInCollection;

  try {
    let meta = null;
    if (props.isConnected) meta = await FangateAPI.updateProduct(p.id, metaPayload);
    let priceResp = null;
    if (priceChanged && props.isConnected) {
      priceResp = await FangateAPI.updateProductPrice(p.id, priceCents);
    }
    if (folderChanged && props.isConnected) {
      await FangateAPI.updateProductFolder(p.id, state.folderId);
    }
    if (collectionChanged && props.isConnected) {
      await FangateAPI.toggleProductCollection(p.id);
    }

    const merged = {
      ...p,
      ...metaPayload,
      ...(meta || {}),
      price:         priceResp?.price ? priceResp.price / 100 : (priceChanged ? priceEur : p.price),
      link:          priceResp?.link  ?? p.link,
      folder_id:     folderChanged     ? state.folderId            : p.folder_id,
      folder:        folderChanged     ? folderNameOf(state.folderId) : p.folder,
      in_collection: collectionChanged ? state.flags.collection    : p.in_collection,
    };
    emit('saved', merged);

    // Stay open — refresh initial snapshots so next save knows what's clean.
    state.initialPrice        = priceEur;
    state.initialFolderId     = state.folderId;
    state.initialInCollection = state.flags.collection;
  } catch (e) {
    state.error = e?.message || 'Save failed.';
  } finally {
    state.saving = false;
  }
};
</script>

<template>
  <!-- Teleport to body so the modal escapes the side-panel's flex/overflow
       ancestors that were clipping its header off the top of the viewport
       (the panel's containing block constrained position:fixed too). -->
  <Teleport to="body">
    <!-- No backdrop dismiss — user must hit Close × to leave -->
    <div v-if="open" class="fp-modal-overlay fp-modal-overlay--upload">
    <div class="fp-iem" @click.stop>
      <!-- Header: Product #ID eyebrow on top, then "[Edit] TITLE: value"
           with the edit button on the LEFT. Preview button sits offset
           from the Close button on the right. -->
      <div class="fp-iem-head">
        <div class="fp-iem-head-inner">
          <div class="fp-iem-head-meta">
            <div class="fp-iem-title">Product #{{ product?.id ?? '—' }}</div>
            <div class="fp-iem-titlerow">
              <span class="fp-iem-titlelabel">Title:</span>
              <input v-if="editingTitle"
                     ref="titleInputRef"
                     v-model="state.title"
                     maxlength="80"
                     placeholder="Product title"
                     class="fp-iem-titleinput"
                     @keydown.enter.prevent="commitEditTitle"
                     @keydown.escape.prevent="commitEditTitle"
                     @blur="commitEditTitle" />
              <span v-else class="fp-iem-titlevalue"
                    @dblclick="startEditTitle"
                    :title="state.title || 'Untitled'">
                {{ state.title || 'Untitled' }}
              </span>
              <!-- Edit button sits to the RIGHT of the title with an extra
                   gap so it reads as separate from the value, not part of it. -->
              <button v-if="!editingTitle"
                      class="fp-iem-titleedit"
                      @click="startEditTitle"
                      title="Edit title">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M12 20h9"/>
                  <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/>
                </svg>
                Edit
              </button>
            </div>
          </div>
        </div>
        <div class="fp-iem-head-actions">
          <button class="fp-iem-actbtn fp-iem-actbtn--view"
                  :disabled="!previewUrl"
                  @click="openPreview"
                  title="Open the content (not the checkout page) in a new tab">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
              <polyline points="15 3 21 3 21 9"/>
              <line x1="10" y1="14" x2="21" y2="3"/>
            </svg>
            View content
          </button>
          <button class="fp-iem-actbtn fp-iem-actbtn--preview"
                  :disabled="!checkoutUrl"
                  @click="openCheckout"
                  title="Open the buyer-facing checkout/sale page in a new tab">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
            </svg>
            Preview link
          </button>
          <button class="fp-iem-close" @click="close" title="Close editor">
            Close
            <span aria-hidden="true">×</span>
          </button>
        </div>
      </div>

      <!-- Body. Title row dropped — the bold title in the header IS the
           editable surface now (click "Edit title" or dbl-click the value). -->
      <div class="fp-iem-body">

        <div class="fp-iem-row-2col">
          <div>
            <label class="fp-iem-label">Price</label>
            <div class="fp-iem-pricewrap">
              <span class="fp-iem-priceprefix">{{ currencySymbol }}</span>
              <!-- type="text" + inputmode="decimal" so no browser-native
                   spinner chevrons render (scoped CSS can't kill them
                   reliably in shadow DOM). saveProductDetail re-validates. -->
              <input v-model="state.price" type="text" inputmode="decimal"
                     placeholder="0.00" class="fp-iem-priceinput" />
              <button class="fp-iem-priceedit"
                      @click.stop="$emit('request-edit-price')"
                      title="Open the price editor">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M12 20h9"/>
                  <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/>
                </svg>
                Edit price
              </button>
            </div>
            <div class="fp-iem-hint">
              Min: {{ formatBalance(FANGATE_MIN_PRICE) }} · Max: {{ formatBalance(FANGATE_MAX_PRICE) }}
            </div>
          </div>
          <div>
            <label class="fp-iem-label">Folder</label>
            <div class="fp-iem-folder">
              <!-- Context-sensitive label:
                   • no folder set → "Add to folder" (call-to-action style)
                   • folder set     → "Change folder · <current name>" -->
              <button class="fp-iem-folderbtn"
                      :class="{ open: state.folderPickerOpen, 'is-empty': !state.folderId }"
                      @click.stop="state.folderPickerOpen = !state.folderPickerOpen">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                </svg>
                <span v-if="!state.folderId" class="fp-iem-folderbtn-cta">+ Add to folder</span>
                <span v-else class="fp-iem-folderbtn-set">
                  Change folder<span class="fp-iem-folderbtn-sep"> · </span><strong>{{ folderLabel }}</strong>
                </span>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2.4" stroke-linecap="round"
                     style="margin-left:auto;flex-shrink:0;">
                  <path d="m6 9 6 6 6-6"/>
                </svg>
              </button>
              <div v-if="state.folderPickerOpen" class="fp-iem-foldermenu" @click.stop>
                <!-- PICK mode: existing folders + "+ New folder" trigger -->
                <template v-if="state.folderMode === 'pick'">
                  <button class="fp-iem-folderrow" @click="pickFolder(null)">
                    <span class="muted">No folder</span>
                    <svg v-if="state.folderId == null" width="11" height="11"
                         viewBox="0 0 24 24" fill="none" stroke="currentColor"
                         stroke-width="2.5" stroke-linecap="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  </button>
                  <div v-if="folderOptions.length" class="fp-iem-foldersep"></div>
                  <button v-for="f in folderOptions" :key="f.id" class="fp-iem-folderrow"
                          @click="pickFolder(f)">
                    <span>{{ f.name }}</span>
                    <svg v-if="Number(state.folderId) === Number(f.id)"
                         width="11" height="11" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  </button>
                  <div class="fp-iem-foldersep"></div>
                  <button class="fp-iem-folderrow fp-iem-folderrow--new"
                          @click="startNewFolder">
                    <span>+ New folder</span>
                  </button>
                </template>
                <!-- NEW mode: inline name input + Save / Cancel -->
                <div v-else class="fp-iem-foldernew">
                  <input v-model="state.newFolderName"
                         placeholder="Folder name"
                         maxlength="40"
                         class="fp-iem-foldernew-input"
                         @keydown.enter.prevent="confirmNewFolder"
                         @keydown.escape.prevent="cancelNewFolder" />
                  <div class="fp-iem-foldernew-row">
                    <button class="fp-iem-foldernew-cancel" @click="cancelNewFolder">Cancel</button>
                    <button class="fp-iem-foldernew-save"
                            :disabled="!state.newFolderName.trim() || state.newFolderSaving"
                            @click="confirmNewFolder">
                      {{ state.newFolderSaving ? 'Creating…' : 'Create' }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="fp-iem-row">
          <label class="fp-iem-label">Public description</label>
          <textarea v-model="state.publicDesc" maxlength="500" rows="3"
                    placeholder="Visible to buyers"
                    class="fp-iem-textarea"/>
        </div>

        <div class="fp-iem-row">
          <label class="fp-iem-label">Internal notes</label>
          <textarea v-model="state.internalNotes" maxlength="500" rows="2"
                    placeholder="Private — only you can see this"
                    class="fp-iem-textarea"/>
        </div>

        <!-- Compliance flag block -->
        <div class="fp-iem-row">
          <label class="fp-iem-label">Access &amp; compliance</label>
          <div class="fp-iem-flags">
            <div class="fp-iem-flag">
              <div>
                <div class="fp-iem-flagtitle">Add to Collection</div>
                <div class="fp-iem-flagdesc">Include this item in your collection page.</div>
              </div>
              <button class="fp-iem-switch" :class="{ on: state.flags.collection }"
                      @click="toggleFlag('collection')">
                <span class="fp-iem-switchknob"></span>
              </button>
            </div>
            <div class="fp-iem-flag">
              <div>
                <div class="fp-iem-flagtitle">AVS (18+)</div>
                <div class="fp-iem-flagdesc">Mark as adult content.</div>
              </div>
              <button class="fp-iem-switch" :class="{ on: state.flags.avs18 }"
                      @click="toggleFlag('avs18')">
                <span class="fp-iem-switchknob"></span>
              </button>
            </div>
            <div class="fp-iem-flag" :class="{ 'is-disabled': !state.flags.avs18 }">
              <div>
                <div class="fp-iem-flagtitle">Require age check</div>
                <div class="fp-iem-flagdesc">Only available when AVS is on.</div>
              </div>
              <button class="fp-iem-switch"
                      :class="{ on: state.flags.agecheck, disabled: !state.flags.avs18 }"
                      :disabled="!state.flags.avs18"
                      @click="toggleFlag('agecheck')">
                <span class="fp-iem-switchknob"></span>
              </button>
            </div>
            <div class="fp-iem-flag">
              <div>
                <div class="fp-iem-flagtitle">Allow download</div>
                <div class="fp-iem-flagdesc">Buyers can download after purchase.</div>
              </div>
              <button class="fp-iem-switch" :class="{ on: state.flags.download }"
                      @click="toggleFlag('download')">
                <span class="fp-iem-switchknob"></span>
              </button>
            </div>
            <div class="fp-iem-flag">
              <div>
                <div class="fp-iem-flagtitle">Requires third-party consent</div>
                <div class="fp-iem-flagdesc">
                  If a third person is visible in this content, consent
                  must be obtained by tagging them — provide their name
                  and email below. Fangate will email this address with
                  the consent request.
                </div>
              </div>
              <button class="fp-iem-switch" :class="{ on: state.flags.shouldConsent }"
                      @click="toggleFlag('shouldConsent')">
                <span class="fp-iem-switchknob"></span>
              </button>
            </div>
          </div>
        </div>

        <!-- Co-creator tagging — same search-always-visible UX as Vault detail -->
        <div v-if="state.flags.shouldConsent" class="fp-iem-row">
          <label class="fp-iem-label">Co-creators &amp; third-party consent</label>
          <div class="fp-iem-co">
            <div v-if="coTags.loading" class="fp-iem-co-hint">Loading tags…</div>
            <div v-else-if="coTags.items.length" class="fp-iem-co-chips">
              <div v-for="t in coTags.items" :key="t.id"
                   class="fp-iem-co-chip"
                   :class="'fp-iem-co-chip--' + (t.status || 'pending')">
                <span>{{ creatorLabelOf(t.tagged_user || t) }}</span>
                <span class="fp-iem-co-chipstatus">{{ t.status || 'pending' }}</span>
              </div>
            </div>

            <input v-model="coTagSearch.query" @input="onCoTagInput"
                   placeholder="Search creators by name or email…"
                   class="fp-iem-input" />

            <div v-if="coTagSearch.loading" class="fp-iem-co-hint">Searching…</div>
            <div v-else-if="coTagSearch.error" class="fp-iem-co-err">{{ coTagSearch.error }}</div>
            <div v-else-if="coTagSearch.results.length" class="fp-iem-co-results">
              <div v-for="c in coTagSearch.results" :key="c.id" class="fp-iem-co-result">
                <div class="fp-iem-co-result-meta">
                  <div class="fp-iem-co-result-name">{{ creatorLabelOf(c) }}</div>
                  <div v-if="c.email" class="fp-iem-co-result-email">{{ c.email }}</div>
                </div>
                <button class="fp-iem-co-tagbtn"
                        :disabled="taggingId === c.id"
                        @click="tagCreatorFromResult(c)">
                  {{ taggingId === c.id ? 'Tagging…' : 'Tag' }}
                </button>
              </div>
            </div>
            <div v-else-if="coTagSearch.query.trim().length >= 2" class="fp-iem-co-empty">
              <div class="fp-iem-co-hint">No matches on Fangate.</div>
              <button v-if="showEmailInvite"
                      class="fp-iem-co-invitebtn"
                      :disabled="invitingEmail"
                      @click="inviteByEmail">
                {{ invitingEmail ? 'Sending…'
                                : `📧 Send consent request to ${coTagSearch.query.trim()}` }}
              </button>
            </div>
            <div v-else-if="!coTags.items.length" class="fp-iem-co-hint">
              Start typing to find a co-creator.
            </div>
          </div>
        </div>

        <div v-if="state.error" class="fp-iem-err">{{ state.error }}</div>
      </div>

      <div class="fp-iem-foot">
        <button class="fp-iem-save"
                :disabled="state.saving"
                @click="save">
          {{ state.saving ? 'Saving…' : 'Save changes' }}
        </button>
      </div>
    </div>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
.fp-modal-overlay {
  position: fixed; inset: 0;
  background: rgba(11, 20, 20, 0.35);
  display: flex; align-items: center; justify-content: center;
  z-index: 9999; padding: 20px;
  &--upload { background: rgba(20, 10, 60, 0.45); backdrop-filter: blur(4px); }
}
.fp-iem {
  background: linear-gradient(180deg, #faf8ff 0%, #fff 100%);
  border-radius: 16px;
  width: 100%; max-width: 580px;
  max-height: 92vh;
  box-shadow: 0 24px 60px rgba(20, 10, 60, 0.30);
  display: flex; flex-direction: column; overflow: hidden;
  position: relative;   // anchor for the absolute top-right Close button
}
.fp-iem-head {
  // flex-wrap lets the action group drop below the title row when the
  // title text is long enough to crowd the buttons. Without wrap they'd
  // overlap or push the title off-screen.
  display: flex; align-items: center; justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  // Extra right padding reserves the top-right corner for the absolute Close
  // button so the title/actions never run under it.
  padding: 16px 96px 16px 22px;
  border-bottom: 1px solid rgba(91, 46, 207, 0.12);
  background: #fff;
  flex-shrink: 0;
}
.fp-iem-head-inner {
  display: flex; align-items: center; gap: 10px;
  // flex-basis (not 0) so a cramped header WRAPS the action group below the
  // title row instead of crushing the title to 0 (which let the buttons overlap
  // it). Triggers on mobile AND a narrow desktop modal.
  min-width: 0; flex: 1 1 220px;
}
.fp-iem-head-meta {
  display: flex; flex-direction: column;
  gap: 4px; min-width: 0; flex: 1;
}
.fp-iem-title {
  font-family: 'Inter', sans-serif;
  font-size: 9.5px; font-weight: 700;
  letter-spacing: 0.12em; text-transform: uppercase;
  color: #5b2ecf;
}
// Inline title editor — "TITLE: bold value [Edit title]" lives in the
// header next to the pencil icon. Replaces the old standalone form row.
.fp-iem-titlerow {
  display: flex; align-items: center; gap: 8px;
  min-width: 0;
}
.fp-iem-titlelabel {
  font-family: 'Inter', sans-serif;
  font-size: 9px; font-weight: 700;
  letter-spacing: 0.12em; text-transform: uppercase;
  color: #7c6fcd; flex-shrink: 0;
}
.fp-iem-titlevalue {
  font-family: 'Inter', sans-serif;
  font-size: 14px; font-weight: 800;
  color: #1a0a6b;
  // No flex:1 — value shrinks to its content so the Edit button sits
  // immediately to the right of "test" instead of being pushed all the
  // way across the row.
  max-width: 100%;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  cursor: pointer;
}
.fp-iem-titleinput {
  flex: 1; min-width: 0;
  font-family: 'Inter', sans-serif;
  font-size: 14px; font-weight: 800;
  color: #1a0a6b;
  background: #fff;
  border: 1.5px solid #7040e0;
  border-radius: 6px;
  padding: 3px 8px;
  outline: none;
  box-shadow: 0 0 0 3px rgba(112, 64, 224, 0.12);
}
.fp-iem-titleedit {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 9px;
  // Extra left margin so the button reads as separated from the title
  // value rather than glued to it.
  margin-left: 8px;
  border-radius: 6px;
  border: 1.5px solid rgba(91, 46, 207, 0.3);
  background: rgba(91, 46, 207, 0.06);
  color: #5b2ecf;
  font-family: 'Inter', sans-serif;
  font-size: 10.5px; font-weight: 700;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.12s, color 0.12s, border-color 0.12s;
  &:hover { background: linear-gradient(135deg, #5b2ecf, #7040e0); border-color: transparent; color: #fff; }
  &:focus { outline: none; }
  &:focus-visible { outline: 2px solid #7040e0; outline-offset: 2px; }
}
// Right-side actions group: View content + Preview link + Close. Tight
// gap between the two link buttons (they're paired), wider gap before
// Close so it reads as the separate dismiss control.
.fp-iem-head-actions {
  display: flex; align-items: center; gap: 6px;
  flex-shrink: 0; flex-wrap: wrap;   // buttons wrap among themselves on a very narrow modal
}
.fp-iem-head-actions > .fp-iem-close { margin-left: 8px; }
// Compact action buttons — smaller than the old Preview pill so two of
// them fit comfortably alongside Close.
.fp-iem-actbtn {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 5px 11px;
  border-radius: 999px;
  font-family: 'Inter', sans-serif;
  font-size: 11px; font-weight: 700;
  cursor: pointer;
  transition: background 0.12s, color 0.12s, border-color 0.12s, transform 0.1s;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:active:not(:disabled) { transform: scale(0.97); }
  &:focus { outline: none; }
  &:focus-visible { outline: 2px solid currentColor; outline-offset: 2px; }
  &--view {
    border: 1.5px solid #047857;
    background: rgba(16, 185, 129, 0.10);
    color: #047857;
    &:hover:not(:disabled) {
      background: #047857; color: #fff;
    }
  }
  &--preview {
    border: 1.5px solid rgba(91, 46, 207, 0.35);
    background: rgba(91, 46, 207, 0.06);
    color: #5b2ecf;
    &:hover:not(:disabled) {
      background: linear-gradient(135deg, #5b2ecf, #7040e0);
      border-color: transparent;
      color: #fff;
    }
  }
}
.fp-iem-close {
  position: absolute; top: 13px; right: 16px; z-index: 3;   // top-right corner (typical modal UX)
  display: inline-flex; align-items: center; gap: 6px;
  padding: 7px 14px;
  border-radius: 999px;
  border: 1.5px solid rgba(91, 46, 207, 0.3);
  background: #fff;
  color: #5b2ecf;
  font-family: 'Inter', sans-serif;
  font-size: 12px; font-weight: 700;
  cursor: pointer;
  transition: background 0.12s, color 0.12s, border-color 0.12s, transform 0.1s;
  > span {
    display: inline-flex; align-items: center; justify-content: center;
    width: 18px; height: 18px;
    border-radius: 50%;
    background: rgba(91, 46, 207, 0.12);
    font-size: 14px; line-height: 1;
    transition: background 0.12s, color 0.12s;
  }
  &:hover {
    background: linear-gradient(135deg, #5b2ecf, #7040e0);
    border-color: transparent;
    color: #fff;
    > span { background: rgba(255, 255, 255, 0.25); color: #fff; }
  }
  &:active { transform: scale(0.97); }
  &:focus { outline: none; }
  &:focus-visible { outline: 2px solid #7040e0; outline-offset: 2px; }
}

.fp-iem-body {
  padding: 16px 22px 18px;
  flex: 1; overflow-y: auto;
  display: flex; flex-direction: column; gap: 12px;
}
.fp-iem-row { display: flex; flex-direction: column; gap: 4px; }
.fp-iem-row-2col {
  display: grid; grid-template-columns: 1fr 1fr; gap: 12px;
}
// Mobile (≤768px): side-by-side field pairs get cramped → stack them.
@media (max-width: 768px) {
  .fp-iem-row-2col { grid-template-columns: 1fr; }
}
.fp-iem-label {
  font-family: 'Inter', sans-serif;
  font-size: 9.5px; font-weight: 700;
  letter-spacing: 0.12em; text-transform: uppercase;
  color: #5b2ecf; margin-bottom: 4px;
}
.fp-iem-input,
.fp-iem-textarea {
  width: 100%; box-sizing: border-box;
  padding: 9px 12px;
  border-radius: 8px;
  border: 1.5px solid rgba(91, 46, 207, 0.18);
  background: #fff;
  font-family: 'Inter', sans-serif;
  font-size: 13px; color: #1a0a6b;
  outline: none;
  &:focus { border-color: #7040e0; box-shadow: 0 0 0 3px rgba(112, 64, 224, 0.12); }
}
.fp-iem-textarea { resize: vertical; min-height: 60px; line-height: 1.45; }

// Price field + inline Edit-price chip
.fp-iem-pricewrap {
  display: flex; align-items: stretch;
  height: 42px; box-sizing: border-box;
  border: 1.5px solid rgba(91, 46, 207, 0.18);
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  transition: border-color 0.12s;
  &:focus-within { border-color: #7040e0; }
}
.fp-iem-priceprefix {
  display: flex; align-items: center; justify-content: center;
  padding: 0 12px;
  font-family: 'Inter', sans-serif;
  font-size: 14px; font-weight: 700;
  color: #7c6fcd; flex-shrink: 0;
}
.fp-iem-priceinput {
  flex: 1; min-width: 0;
  height: 100%; line-height: 38px;
  box-sizing: border-box;
  border: none; background: transparent; outline: none;
  font-family: 'Inter', sans-serif;
  font-size: 14px; color: #1a0a6b; font-weight: 600;
  padding: 0 10px 0 0;
  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none; margin: 0;
  }
}
.fp-iem-priceedit {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 0 12px;
  border: none;
  border-left: 1px solid rgba(91, 46, 207, 0.18);
  background: rgba(16, 185, 129, 0.10);
  color: #047857;
  font-family: 'Inter', sans-serif;
  font-size: 11.5px; font-weight: 700;
  cursor: pointer; white-space: nowrap; flex-shrink: 0;
  transition: background 0.1s, color 0.1s;
  &:hover { background: #047857; color: #fff; }
  &:focus { outline: none; }
}
.fp-iem-hint {
  font-family: 'Inter', sans-serif;
  font-size: 11px; color: #7c6fcd;
}

// Folder
.fp-iem-folder { position: relative; }
.fp-iem-folderbtn {
  display: flex; align-items: center; gap: 8px;
  width: 100%; box-sizing: border-box;
  height: 42px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1.5px solid rgba(91, 46, 207, 0.18);
  background: #fff;
  font-family: 'Inter', sans-serif;
  font-size: 13px; color: #1a0a6b;
  cursor: pointer; text-align: left;
  transition: border-color 0.12s, background 0.12s;
  &:hover, &.open { border-color: #7040e0; }
  // Empty state — dashed border + lighter background reads as CTA, not
  // "current value". Hover/open keeps the same accent treatment.
  &.is-empty {
    border-style: dashed;
    background: rgba(91, 46, 207, 0.03);
    color: #5b2ecf;
  }
  .muted { color: #9b90e0; font-style: italic; }
}
.fp-iem-folderbtn-cta {
  font-weight: 700;
  color: #5b2ecf;
}
.fp-iem-folderbtn-set {
  font-weight: 500;
  color: #7c6fcd;
  strong { color: #1a0a6b; font-weight: 700; }
}
.fp-iem-folderbtn-sep { color: #9b90e0; }
.fp-iem-foldermenu {
  position: absolute; top: calc(100% + 4px); left: 0; right: 0;
  z-index: 10;
  background: #fff;
  border: 1px solid rgba(91, 46, 207, 0.16);
  border-radius: 9px;
  box-shadow: 0 8px 22px rgba(20, 10, 60, 0.14);
  padding: 4px;
  max-height: 220px; overflow-y: auto;
}
.fp-iem-folderrow {
  display: flex; align-items: center; justify-content: space-between;
  width: 100%;
  padding: 7px 9px;
  border-radius: 6px; border: none; background: transparent;
  font-family: 'Inter', sans-serif;
  font-size: 12.5px; color: #1a0a6b;
  cursor: pointer; text-align: left;
  &:hover { background: rgba(91, 46, 207, 0.06); }
  .muted { color: #9b90e0; font-style: italic; }
}
.fp-iem-foldersep { height: 1px; background: rgba(91, 46, 207, 0.10); margin: 4px 6px; }

// "+ New folder" trigger — accent color so it pops vs. the regular folder
// rows but doesn't hijack the existing-folder styling.
.fp-iem-folderrow--new {
  color: #5b2ecf;
  font-weight: 700;
}

// Inline new-folder editor — replaces the picker content when 'new' mode.
.fp-iem-foldernew {
  display: flex; flex-direction: column; gap: 8px;
  padding: 4px;
}
.fp-iem-foldernew-input {
  width: 100%; box-sizing: border-box;
  padding: 8px 10px;
  border-radius: 7px;
  border: 1.5px solid #7040e0;
  background: #fff;
  font-family: 'Inter', sans-serif;
  font-size: 12.5px; color: #1a0a6b;
  outline: none;
  &:focus { box-shadow: 0 0 0 3px rgba(112, 64, 224, 0.12); }
}
.fp-iem-foldernew-row {
  display: flex; gap: 6px; justify-content: flex-end;
}
.fp-iem-foldernew-cancel,
.fp-iem-foldernew-save {
  font-family: 'Inter', sans-serif;
  font-size: 11.5px; font-weight: 700;
  padding: 6px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.1s, color 0.1s, border-color 0.1s;
}
.fp-iem-foldernew-cancel {
  border: 1.5px solid rgba(91, 46, 207, 0.25);
  background: #fff;
  color: #5b2ecf;
  &:hover { border-color: #5b2ecf; background: rgba(91, 46, 207, 0.06); }
}
.fp-iem-foldernew-save {
  border: 1.5px solid #5b2ecf;
  background: linear-gradient(135deg, #5b2ecf, #7040e0);
  color: #fff;
  &:hover:not(:disabled) { background: linear-gradient(135deg, #4a1eaa, #5b2ecf); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}

// Compliance flags
.fp-iem-flags {
  display: flex; flex-direction: column;
  border: 1px solid rgba(91, 46, 207, 0.12);
  border-radius: 10px;
  background: #fff;
  overflow: hidden;
}
.fp-iem-flag {
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 10px; padding: 10px 14px;
  border-bottom: 1px solid rgba(91, 46, 207, 0.08);
  &:last-child { border-bottom: none; }
  &.is-disabled { opacity: 0.5; }
}
.fp-iem-flagtitle {
  font-family: 'Inter', sans-serif;
  font-size: 12.5px; font-weight: 700; color: #1a0a6b;
}
.fp-iem-flagdesc {
  font-family: 'Inter', sans-serif;
  font-size: 11px; color: #7c6fcd; margin-top: 1px;
}
.fp-iem-switch {
  flex-shrink: 0;
  position: relative; width: 36px; height: 20px;
  border-radius: 999px; border: none; padding: 0;
  background: rgba(91, 46, 207, 0.18);
  cursor: pointer; transition: background 0.12s;
  &.on { background: linear-gradient(135deg, #5b2ecf, #7040e0); }
  &.disabled { cursor: not-allowed; }
}
.fp-iem-switchknob {
  position: absolute; top: 2px; left: 2px;
  width: 16px; height: 16px; border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 3px rgba(11, 20, 20, 0.2);
  transition: transform 0.15s;
  .fp-iem-switch.on & { transform: translateX(16px); }
}

// Co-creator block
.fp-iem-co {
  display: flex; flex-direction: column; gap: 8px;
  padding: 12px;
  border: 1.5px dashed rgba(16, 185, 129, 0.45);
  border-radius: 9px;
  background: linear-gradient(180deg, rgba(16, 185, 129, 0.06), transparent);
}
.fp-iem-co-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.fp-iem-co-chip {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(16, 185, 129, 0.10);
  border: 1px solid rgba(16, 185, 129, 0.32);
  color: #047857;
  font-family: 'Inter', sans-serif;
  font-size: 11.5px; font-weight: 600;
  &--accepted  { background: rgba(16, 185, 129, 0.16); }
  &--pending   { background: rgba(245, 158, 11, 0.10); border-color: rgba(245, 158, 11, 0.40); color: #b45309; }
  &--declined  { background: rgba(220, 38, 38, 0.10); border-color: rgba(220, 38, 38, 0.40); color: #b91c1c; }
}
.fp-iem-co-chipstatus {
  font-size: 9.5px; font-weight: 800;
  letter-spacing: 0.04em; text-transform: uppercase;
  margin-left: 2px;
}
.fp-iem-co-hint {
  font-family: 'Inter', sans-serif;
  font-size: 11px; color: #7c6fcd; font-style: italic;
}
.fp-iem-co-err {
  font-family: 'Inter', sans-serif;
  font-size: 11.5px; color: #b91c1c;
  padding: 6px 10px; border-radius: 7px;
  background: rgba(220, 38, 38, 0.08);
}
.fp-iem-co-empty { display: flex; flex-direction: column; gap: 8px; }
.fp-iem-co-results {
  background: #fff;
  border: 1px solid rgba(91, 46, 207, 0.12);
  border-radius: 8px;
  padding: 4px;
  max-height: 180px; overflow-y: auto;
}
.fp-iem-co-result {
  display: flex; align-items: center; gap: 8px;
  padding: 6px 8px; border-radius: 6px;
  &:hover { background: rgba(91, 46, 207, 0.04); }
}
.fp-iem-co-result-meta { flex: 1; min-width: 0; }
.fp-iem-co-result-name {
  font-family: 'Inter', sans-serif;
  font-size: 12px; font-weight: 700; color: #1a0a6b;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.fp-iem-co-result-email {
  font-family: 'Inter', sans-serif;
  font-size: 10.5px; color: #7c6fcd;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.fp-iem-co-tagbtn,
.fp-iem-co-invitebtn {
  padding: 5px 11px;
  border-radius: 7px; border: none;
  background: rgba(16, 185, 129, 0.12);
  color: #047857;
  font-family: 'Inter', sans-serif;
  font-size: 11.5px; font-weight: 700;
  cursor: pointer; white-space: nowrap; flex-shrink: 0;
  transition: background 0.1s, color 0.1s;
  &:hover:not(:disabled) { background: #047857; color: #fff; }
  &:disabled { opacity: 0.6; cursor: wait; }
}
.fp-iem-co-invitebtn {
  align-self: flex-start;
  padding: 7px 12px;
  border: 1.5px solid rgba(16, 185, 129, 0.4);
  background: rgba(16, 185, 129, 0.06);
}

// Footer / Save
.fp-iem-err {
  padding: 8px 12px; border-radius: 8px;
  background: #fee2e2; color: #991b1b;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
}
.fp-iem-foot {
  padding: 12px 22px;
  border-top: 1px solid rgba(91, 46, 207, 0.12);
  background: #fff;
  display: flex; justify-content: flex-end; flex-shrink: 0;
}
.fp-iem-save {
  display: inline-flex; align-items: center; justify-content: center;
  gap: 7px;
  padding: 10px 20px;
  border-radius: 9px; border: none;
  background: linear-gradient(135deg, #1a0a6b, #5b2ecf, #7040e0);
  color: #fff;
  font-family: 'Inter', sans-serif;
  font-size: 13px; font-weight: 800;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(91, 46, 207, 0.32);
  transition: box-shadow 0.15s, transform 0.1s;
  &:hover:not(:disabled) {
    box-shadow: 0 6px 20px rgba(91, 46, 207, 0.42);
  }
  &:active:not(:disabled) { transform: scale(0.98); }
  &:disabled { opacity: 0.65; cursor: not-allowed; }
}
</style>
